import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { FeatureFlagService, INSIGHT_STUDIO_FLAG_KEY } from '../feature-flags/feature-flag.service';
import { TenantContextService } from '../tenant/tenant-context.service';
import { TenantLifecycleService } from '../tenant/tenant-lifecycle.service';

/**
 * Gate for the Insight Studio surfaces: the `insight-studio-enabled` flag must be
 * on and the user privileged (`super_user` or `admin`).
 *
 * Awaits the memoized tenant bootstrap before reading `isPrivileged`, so a cold
 * deep-link by a privileged user is not bounced while the user type is unresolved.
 */
export const insightStudioGuard: CanActivateFn = async () => {
  const flags = inject(FeatureFlagService);
  const router = inject(Router);
  const lifecycle = inject(TenantLifecycleService);
  const tenantContext = inject(TenantContextService);

  await flags.ready();
  if (!flags.isEnabled(INSIGHT_STUDIO_FLAG_KEY)) {
    return router.createUrlTree(['/home']);
  }

  try {
    await firstValueFrom(lifecycle.bootstrapTenantContext());
  } catch {
    // No tenant context to act on — deny conservatively.
    return router.createUrlTree(['/home']);
  }

  return tenantContext.isPrivileged() || router.createUrlTree(['/home']);
};
